import { AdminUsersAction } from "@/constants/admin-accounts";
import { ModalWidth } from "@/constants/modals";
import AddAdminUserModal from "@/components/modals/AddAdminUserModal.vue";
import type { AdminUser } from "@/_store/admin-users.module";
import { type DialogConfig, type DialogDataConfig, useDialogsStore } from "@/_store/dialogs.module";
import { RolePermissionsScope, WorkspaceManagementScopeSections } from "@/_store/roles.module";
import {
  componentDialogsConfigConstructor,
  confirmationDialogsConfigConstructor,
  isActionRestricted,
  isWorkspaceFrozenOrActionRestricted,
  canOverrideGlobalPolicy,
  isWorkspaceNewAndInDemoMode,
  openMovingToTrialModal,
} from "@/_helpers/utils";
import {
  AccessPermissionAction,
  AccessPermissionType,
  ServiceAction,
} from "@/constants/cloud-apps";
import AddAccessPermissionModal from "@/components/modals/AddAccessPermissionModal.vue";
import { useAccessPermissionsStore } from "@/_store/cloud-apps/access-permissions.module.ts";
import {
  LabelAction,
  DevicePostureAction,
  DevicePostureOsType,
  type DevicePostureSettings,
} from "@/constants/devices";
import AddLabelModal from "@/components/modals/AddLabelModal.vue";
import { type DeviceLabel, useDevicesSettingsStore } from "@/_store/devices-settings.module";
import AddOrEditPostureModal from "@/components/modals/device-posture/AddOrEditPostureModal.vue";
import { useDevicesPostureStore } from "@/_store/device-posture/devices-posture-settings.module";
import { GlobalPolicySection } from "@/constants/msp.ts";
import SetIndustryModal from "@/components/modals/data-governance/SetIndustryModal.vue";
import { usePrivacySensitiveDataStore } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module";
import { EndpointDataGovernanceAction } from "@/constants/endpoint-data-governance";
import { useUserDataGovernanceModule } from "@/_store/user-data-governance/user-data-governance.module";
import { UserDataGovernanceAction } from "@/constants/user-data-governance";
import ConnectServiceModal from "@/views/settings/cloud-apps/modals/ConnectServiceModal.vue";
import type { ConnectAppModalConfigItem } from "@/views/settings/cloud-apps/CloudAppsPage.vue";
import { useCloudAppsStore } from "@/_store/cloud-apps/cloud-apps.module";
import { DemoWillStartModal } from "@/constants/tutorial.ts";
import router from "@/_helpers/router.ts";
import { i18n } from "@/plugins/i18n";
import { SubscriptionModule } from "@/constants/workplaces.ts";
import { AgentDeploymentAction } from "@/constants/devices";
import type { DeviceVersionItem } from "@/_store/devices-settings.module";
import { useSnackbarStore } from "@/_store/snackbar.module";
import CopyLinkModal from "@/components/modals/CopyLinkModal.vue";
import { createDownloadLink, handleDownloadAction, copyToClipboard } from "@/_helpers/utils";
import CloudAppConnectionStatusModal from "@/views/settings/cloud-apps/modals/CloudAppConnectionStatusModal.vue";

export const getAddAdminUserDialogConfig = (
  addAdminUserCallback: (data: DialogDataConfig<AdminUser>) => Promise<void>
): DialogConfig => {
  return componentDialogsConfigConstructor({
    action: AdminUsersAction.ADD,
    component: AddAdminUserModal,
    width: ModalWidth.LARGE,
    callback: addAdminUserCallback,
    disable: isActionRestricted(
      RolePermissionsScope.WORKSPACE_MANAGEMENT,
      WorkspaceManagementScopeSections.ADMIN_USERS,
      "add"
    ),
  });
};

export const openConnectCloudAppsModal = (
  action: ServiceAction,
  item: Partial<ConnectAppModalConfigItem>,
  openWithDemoConfirmation = false
) => {
  const dialogsStore = useDialogsStore();
  const cloudAppsStore = useCloudAppsStore();
  const actionNotAllowed = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.WORKSPACE_MANAGEMENT,
    WorkspaceManagementScopeSections.CLOUD_APPS
  );

  switch (action) {
    case ServiceAction.GRANT_PRIVILEGES:
    case ServiceAction.CONNECT: {
      const baseConfig = componentDialogsConfigConstructor({
        width: ModalWidth.LARGE,
        action,
        item: { ...item, partialScan: true },
        component: ConnectServiceModal,
        disable: actionNotAllowed,
        callback: () => {},
        closeCallback: () => router.replace({ query: {} }),
      });

      const dialogExtras = {
        header: {
          title: i18n.global.t(`modals.${ServiceAction.CONNECT}.noServiceTitle`),
          close: true,
        },
        footer: null,
        scrollable: true,
      };

      const dialogConfig = { ...baseConfig, ...dialogExtras };

      if (
        openWithDemoConfirmation &&
        action === ServiceAction.CONNECT &&
        isWorkspaceNewAndInDemoMode()
      ) {
        return openMovingToTrialModal(DemoWillStartModal.CLOUD_APPS, () => {
          dialogsStore.openDialog(dialogConfig);
        });
      }

      dialogsStore.openDialog(dialogConfig);
      break;
    }
    case ServiceAction.REMOVE:
    case ServiceAction.DISCONNECT: {
      dialogsStore.openDialog({
        ...confirmationDialogsConfigConstructor({
          action: ServiceAction.DISCONNECT,
          text: i18n.global.t(`modals.${ServiceAction.DISCONNECT}.description`, {
            service: i18n.global.t(`services.${item.name}`),
          }),
          callback: () => cloudAppsStore.disconnect(item.name!),
          disable: isActionRestricted(
            RolePermissionsScope.WORKSPACE_MANAGEMENT,
            WorkspaceManagementScopeSections.CLOUD_APPS
          ),
        }),
      });
      break;
    }
    case ServiceAction.CONNECTION_STATUS: {
      dialogsStore.openDialog(
        componentDialogsConfigConstructor({
          width: ModalWidth.LARGE,
          action: ServiceAction.CONNECTION_STATUS,
          item,
          component: CloudAppConnectionStatusModal,
          disable: false,
          callback: () => {},
          hideFooter: true,
        })
      );
      break;
    }
  }
};

export const getAddAccessPermissionsDialogConfig = (
  item: {
    action: AccessPermissionAction;
    userType: AccessPermissionType;
  } = {
    action: AccessPermissionAction.ADD,
    userType: AccessPermissionType.ALL_USERS,
  },
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.CLOUD_SECURITY
  )
) => {
  return componentDialogsConfigConstructor({
    action: item.action,
    item: {
      userType: item.userType,
    },
    component: AddAccessPermissionModal,
    scrollable: true,
    callback: useAccessPermissionsStore().addAccessPermissions,
    width: ModalWidth.LARGE,
    disable,
  });
};

export const getAddLabelDialogConfig = (
  action: LabelAction.EDIT_LABEL | LabelAction.CREATE_LABEL,
  item?: DeviceLabel,
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.WORKSPACE_MANAGEMENT,
    WorkspaceManagementScopeSections.DEVICES
  )
): DialogConfig => {
  const devicesSettingsStore = useDevicesSettingsStore();
  return componentDialogsConfigConstructor({
    component: AddLabelModal,
    item,
    action,
    width: ModalWidth.X_SMALL,
    callback:
      action === LabelAction.CREATE_LABEL
        ? devicesSettingsStore.createLabel
        : devicesSettingsStore.editLabel,
    disable,
  });
};

export const getAddDevicePostureDialogConfig = (
  {
    osType,
    action,
  }: {
    osType: DevicePostureOsType;
    action: DevicePostureAction;
  },
  labels: DeviceLabel[] = [],
  policyType?: DevicePostureSettings,
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.ENDPOINT_SECURITY
  )
): DialogConfig => {
  const devicesPostureModule = useDevicesPostureStore();
  const isEditRestricted = !canOverrideGlobalPolicy(GlobalPolicySection.ENDPOINT_SECURITY);

  const config = componentDialogsConfigConstructor({
    action,
    item: {
      osType,
      labels,
      policyType,
    },
    width: ModalWidth.LARGE,
    component: AddOrEditPostureModal,
    callback: devicesPostureModule.addDevicePosturePolicy,
    disable: isEditRestricted || disable,
    scrollable: true,
  });

  const translatedOsType = i18n.global.t(`devicesSettings.devicePostureTab.osTypes.${osType}`);
  if (isEditRestricted) {
    config.disclaimer = i18n.global.t("general.settingsConfiguredByAdmin");
  }

  return {
    ...config,
    header: {
      title: i18n.global.t(`modals.${action}.title`, { osType: translatedOsType }),
    },
  };
};

export const getSetIndustryDialogConfig = (
  item?: { industries: string[] },
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.ENDPOINT_DATA_GOVERNANCE
  )
): DialogConfig => {
  const privacySensitiveDataStore = usePrivacySensitiveDataStore();
  const isEditRestricted = !canOverrideGlobalPolicy(GlobalPolicySection.ENDPOINT_DATA_GOVERNANCE);

  const config = componentDialogsConfigConstructor({
    action: EndpointDataGovernanceAction.SET_INDUSTRY,
    component: SetIndustryModal,
    width: ModalWidth.LARGE,
    disable: isEditRestricted || disable,
    item: item || { industries: [] },
    callback: async (val: { selectedIndustries: string[] }) => {
      await privacySensitiveDataStore.updatePrivacyConfig(val.selectedIndustries);
    },
  });

  if (isEditRestricted) {
    config.disclaimer = i18n.global.t("general.settingsConfiguredByAdmin");
  }

  return config;
};

export const getSetUserDataIndustryDialogConfig = (
  item?: { industries: string[]; locations?: string[] },
  disable: boolean = isWorkspaceFrozenOrActionRestricted(
    RolePermissionsScope.PROTECTION,
    SubscriptionModule.USER_DATA_GOVERNANCE
  )
): DialogConfig => {
  const userDataStore = useUserDataGovernanceModule();
  const isEditRestricted = !canOverrideGlobalPolicy(GlobalPolicySection.USER_DATA_GOVERNANCE);

  const config = componentDialogsConfigConstructor({
    action: UserDataGovernanceAction.SET_INDUSTRY,
    component: SetIndustryModal,
    width: ModalWidth.LARGE,
    disable: isEditRestricted || disable,
    item: item || { selectedIndustries: [] },
    callback: async (val: { selectedIndustries: string[] }) => {
      await userDataStore.updatePrivacyConfig(val.selectedIndustries);
    },
  });

  if (isEditRestricted) {
    config.disclaimer = i18n.global.t("general.settingsConfiguredByAdmin");
  }

  return config;
};

export async function onAgentDeploymentActionClick(
  action: AgentDeploymentAction,
  item: DeviceVersionItem,
  downloadClientPageLink: string
): Promise<void> {
  const dialogsStore = useDialogsStore();
  const snackbarStore = useSnackbarStore();

  const handleAction = async () => {
    const downloadLink = await createDownloadLink(item, downloadClientPageLink);
    switch (action) {
      case AgentDeploymentAction.COPY_LINK:
        dialogsStore.openDialog(
          componentDialogsConfigConstructor({
            item: { inviteLink: downloadLink },
            action: AdminUsersAction.COPY_INVITE_LINK,
            component: CopyLinkModal,
            width: ModalWidth.LARGE,
            disable: isWorkspaceFrozenOrActionRestricted(
              RolePermissionsScope.WORKSPACE_MANAGEMENT,
              WorkspaceManagementScopeSections.DEVICES
            ),
            callback: () => {
              copyToClipboard(downloadLink);
              snackbarStore.addGenericSuccess(
                i18n.global.t("snackbar.messages.general.copiedToClipboard")
              );
            },
          })
        );
        break;
      case AgentDeploymentAction.DOWNLOAD:
        await handleDownloadAction(downloadLink);
        break;
    }
  };

  if (isWorkspaceNewAndInDemoMode()) {
    return openMovingToTrialModal(DemoWillStartModal.DEVICES, handleAction);
  }

  await handleAction();
}
